import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Fingerprint, Loader2, MinusCircle, Sparkles, XCircle, Trophy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Die } from "@/components/Dice";
import { api, errCode } from "@/lib/api";
import { replayGame } from "@/lib/fair";
import { useI18n } from "@/i18n/I18nContext";

const EMPTY = { gameId: "", seed: "", seedHash: "", players: "" };
const inputCls = "h-12 rounded-xl border-white/10 bg-white/5 font-mono text-sm";

const Check = ({ ok, label, testId }) => (
  <div className={`flex items-center gap-2 text-sm ${ok === null ? "text-slate-400" : ok ? "text-emerald-400" : "text-red-400"}`} data-testid={testId}>
    {ok === null ? <MinusCircle className="h-4 w-4" /> : ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {label}
  </div>
);

const RoundCard = ({ round, names }) => {
  const { t } = useI18n();
  return (
    <div className="surface p-5" data-testid={`verify-round-${round.round}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
        {round.round === 0 ? t("verify.round0") : t("verify.reroll", { n: round.round })}
      </p>
      <ul className="mt-3 divide-y divide-white/5">
        {round.results.map((r) => (
          <li key={r.player_id} className="flex items-center justify-between gap-3 py-3">
            <span className="min-w-0 truncate text-sm font-semibold">{names[r.player_id] || r.player_id}</span>
            <div className="flex items-center gap-3">
              {r.dice.map((d, i) => <Die key={i} value={d} size={30} flat ivory={i % 2 === 1} testId={`verify-die-${round.round}-${r.player_id}-${i}`} />)}
              <span className={`w-8 text-right font-mono font-semibold ${round.leaders.includes(r.player_id) ? "text-amber-400" : "text-slate-400"}`}>{r.total}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Result = ({ res, form, names, server }) => {
  const { t } = useI18n();
  const hashOk = form.seedHash.trim() ? res.seed_hash === form.seedHash.trim().toLowerCase() : null;
  const same = (a) => JSON.stringify(a);
  const serverOk = server && same([server.order, server.rounds, server.winners]) === same([res.order, res.rounds, res.winners]);
  const hashLabel = hashOk === null ? t("verify.hashNone") : hashOk ? t("verify.hashOk") : t("verify.hashBad");
  return (
    <section className="space-y-4" data-testid="verify-result">
      <div className="surface space-y-2 p-5">
        <Check ok={hashOk} label={hashLabel} testId="verify-hash-check" />
        {server && <Check ok={serverOk} label={serverOk ? t("verify.serverOk") : t("verify.serverBad")} testId="verify-server-check" />}
        <p className="break-all pt-2 font-mono text-[11px] text-slate-500">sha256 = {res.seed_hash}</p>
        <p className="pt-2 text-sm text-slate-400">{t("verify.order")} <span className="text-slate-200" data-testid="verify-order">{res.order.map((p) => names[p] || p).join(" → ")}</span></p>
        <p className="flex items-center gap-2 pt-1 text-sm font-semibold text-amber-400" data-testid="verify-winners">
          <Trophy className="h-4 w-4" /> {t("verify.winner")} {res.winners.map((p) => names[p] || p).join(", ")}
        </p>
      </div>
      {res.rounds.map((r) => <RoundCard key={r.round} round={r} names={names} />)}
    </section>
  );
};

export default function VerifyPage() {
  const { t, tErr } = useI18n();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [names, setNames] = useState({});
  const [server, setServer] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setServer(null);
    setRes(null);
  };

  const fill = (d, nameMap = {}) => {
    setForm({ gameId: d.game_id, seed: d.seed || "", seedHash: d.seed_hash, players: d.player_ids.join(", ") });
    setNames(nameMap);
    setServer(d.rounds ? { order: d.order, rounds: d.rounds, winners: d.winners, variant: d.variant } : null);
    setRes(null);
  };

  useEffect(() => {
    const gid = params.get("game");
    if (!gid) return;
    api.get(`/fair/games/${gid}`).then(({ data }) => {
      fill(data, Object.fromEntries(data.players.map((p) => [p.user_id, p.pseudo])));
      if (!data.seed) toast.info(t("verify.seedHidden"));
    }).catch((e) => toast.error(tErr(errCode(e))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const demo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fair/demo");
      fill(data, Object.fromEntries(data.player_ids.map((p) => [p, p.split("-")[0]])));
    } catch (e) {
      toast.error(tErr(errCode(e)));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    const playerIds = form.players.split(",").map((s) => s.trim()).filter(Boolean);
    if (playerIds.length < 2 || !form.seed || !form.gameId) return toast.error(t("verify.missing"));
    setBusy(true);
    try {
      const v = server?.variant || {};
      setRes(await replayGame({ seed: form.seed.trim(), gameId: form.gameId.trim(), playerIds, diceCount: v.dice_count ?? 2, maxRerolls: v.max_rerolls ?? 3 }));
    } catch {
      toast.error(t("verify.unsupported"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 rise" data-testid="verify-page">
      <div>
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-400"><Fingerprint className="h-4 w-4" /> {t("landing.fairTitle")}</p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("verify.title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{t("verify.intro")}</p>
      </div>
      <button onClick={demo} disabled={busy} data-testid="verify-demo-btn"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-amber-500/40 font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
        <Sparkles className="h-4 w-4" /> {t("verify.demo")}
      </button>
      <form onSubmit={verify} className="surface space-y-4 p-5 sm:p-6" data-testid="verify-form">
        <div className="space-y-2"><Label className="text-slate-300">{t("verify.gameId")}</Label><Input value={form.gameId} onChange={set("gameId")} className={inputCls} data-testid="verify-game-id-input" /></div>
        <div className="space-y-2"><Label className="text-slate-300">{t("verify.seedHash")}</Label><Input value={form.seedHash} onChange={set("seedHash")} className={inputCls} data-testid="verify-seed-hash-input" /></div>
        <div className="space-y-2"><Label className="text-slate-300">{t("verify.seed")}</Label><Input value={form.seed} onChange={set("seed")} className={inputCls} data-testid="verify-seed-input" /></div>
        <div className="space-y-2"><Label className="text-slate-300">{t("verify.players")}</Label><Input value={form.players} onChange={set("players")} className={inputCls} data-testid="verify-players-input" /></div>
        <button type="submit" disabled={busy} data-testid="verify-submit-btn"
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-display font-bold text-[#0B0D12] transition-transform active:scale-[0.98] disabled:opacity-60">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("verify.submit")}
        </button>
        <p className="text-center text-xs text-slate-500">{t("verify.local")}</p>
      </form>
      {res && <Result res={res} form={form} names={names} server={server} />}
    </div>
  );
}
